import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_DOMAIN, API_ROUTES } from '../../Reusable_Components/Config/apiConfig';
import * as listCache from '../../../services/listCache';

const SEVERITY = {
  critical: { label: 'Critical',  bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca' },
  high:     { label: 'High',      bg: '#fff7ed', fg: '#c2410c', border: '#fed7aa' },
  medium:   { label: 'Medium',    bg: '#fffbeb', fg: '#a16207', border: '#fde68a' },
  low:      { label: 'Low',       bg: '#f0fdf4', fg: '#15803d', border: '#bbf7d0' },
};

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const getSeverity = (days) => {
  if (days === null) return null;
  if (days > 30) return 'critical';
  if (days > 14) return 'high';
  if (days > 7)  return 'medium';
  return 'low';
};

const getSeverityFromDueDate = (dueDateStr) => {
  if (!dueDateStr) return null;
  const daysOverdue = Math.floor((Date.now() - new Date(dueDateStr).getTime()) / (1000 * 60 * 60 * 24));
  if (daysOverdue < 0) return null;
  if (daysOverdue === 0) return 'medium';
  if (daysOverdue <= 3)  return 'medium';
  if (daysOverdue <= 7)  return 'high';
  return 'critical';
};

const SeverityBadge = ({ level }) => {
  const s = SEVERITY[level];
  if (!s) return null;
  return (
    <span style={{
      background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
      borderRadius: 99, padding: '2px 10px', fontSize: 11, fontWeight: 600,
    }}>
      {s.label}
    </span>
  );
};

const AlertRow = ({ icon, title, subtitle, severity, to, age }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
    borderBottom: '1px solid var(--border)', background: 'var(--bg-card)',
  }}>
    <div style={{
      width: 36, height: 36, borderRadius: 8, background: SEVERITY[severity]?.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-main)', marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>{subtitle}</div>
    </div>
    <SeverityBadge level={severity} />
    {age !== null && (
      <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', marginLeft: 8 }}>
        {age}d ago
      </span>
    )}
    {to && (
      <Link to={to} className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }}>View</Link>
    )}
  </div>
);

const SLAAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ critical: 0, high: 0, medium: 0, low: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    const ENTRIES = [
      ['tasks',         API_DOMAIN + API_ROUTES.task         + '?page_size=50'],
      ['followUps',     API_DOMAIN + API_ROUTES.opportunityFollowUp + '?page_size=50'],
      ['leads',         API_DOMAIN + API_ROUTES.lead         + '?page_size=50'],
      ['quoteFups',     API_DOMAIN + API_ROUTES.quoteFollowUp + '?page_size=50'],
      ['slaViolations', API_DOMAIN + 'sla/violations/?page_size=50'],
      ['slaRules',      API_DOMAIN + 'sla/sla-rules/?page_size=50'],
    ];

    const getPayload = (d) => {
      if (!d) return [];
      if (Array.isArray(d)) return d;
      if (Array.isArray(d.data)) return d.data;
      if (Array.isArray(d.results)) return d.results;
      if (d.data && Array.isArray(d.data.results)) return d.data.results;
      return [];
    };

    const buildAndSet = (dataMap) => {
      const all = [];

      // Tasks — overdue by due_date
      getPayload(dataMap.tasks).forEach(task => {
        const sev = getSeverityFromDueDate(task.due_date || task.deadline || task.end_date);
        if (sev) all.push({
          id: `task-${task.id}`,
          icon: '✅',
          title: task.title || task.name || `Task #${task.id}`,
          subtitle: `Due: ${task.due_date || task.deadline || '—'}`,
          severity: sev,
          to: `/task/details/${btoa(task.id)}`,
          age: daysSince(task.due_date || task.deadline),
        });
      });

      // Opportunity follow-ups — overdue
      getPayload(dataMap.followUps).forEach(f => {
        const sev = getSeverityFromDueDate(f.follow_up_date || f.due_date || f.scheduled_date);
        if (sev) all.push({
          id: `followup-${f.id}`,
          icon: '📞',
          title: f.subject || f.title || `Follow-up #${f.id}`,
          subtitle: `Scheduled: ${f.follow_up_date || f.due_date || '—'}`,
          severity: sev,
          to: `/opportunity/follow-up/details/${btoa(f.id)}`,
          age: daysSince(f.follow_up_date || f.due_date),
        });
      });

      // Quote follow-ups — overdue, skip completed
      getPayload(dataMap.quoteFups).forEach(f => {
        if (f.completed) return;
        const sev = getSeverityFromDueDate(f.follow_up_date);
        if (sev) all.push({
          id: `quote-followup-${f.id}`,
          icon: '📄',
          title: f.follow_up_notes ? f.follow_up_notes.slice(0, 60) : `Quote Follow-up #${f.id}`,
          subtitle: `Scheduled: ${f.follow_up_date || '—'}`,
          severity: sev,
          to: `/quote/follow-up/details/${btoa(f.id)}`,
          age: daysSince(f.follow_up_date),
        });
      });

      // Stale leads — no activity for 14+ days
      getPayload(dataMap.leads).forEach(lead => {
        const age = daysSince(lead.updated_at || lead.created_at);
        const sev = getSeverity(age);
        if (sev && sev !== 'low') all.push({
          id: `lead-${lead.id}`,
          icon: '⚡',
          title: [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.name || `Lead #${lead.id}`,
          subtitle: `No activity for ${age} days`,
          severity: sev,
          to: `/lead/details/${btoa(lead.id)}`,
          age,
        });
      });

      // Real SLA engine violations — active/escalated only
      const rules = getPayload(dataMap.slaRules);
      const ruleNames = Object.fromEntries(rules.map(r => [r.id, r.name]));
      getPayload(dataMap.slaViolations).forEach(v => {
        if (v.status === 'resolved') return;
        const sev = v.status === 'escalated' ? 'critical' : (rules.find(r => r.id === v.sla_rule)?.priority <= 1 ? 'high' : 'medium');
        const entityLabel = v.opportunity ? `Opportunity #${v.opportunity}` : v.lead ? `Lead #${v.lead}` : v.customer ? `Customer #${v.customer}` : `Violation #${v.id}`;
        const to = v.opportunity ? `/opportunity/details/${btoa(v.opportunity)}`
          : v.lead ? `/lead/details/${btoa(v.lead)}`
          : v.customer ? `/customer/details/${btoa(v.customer)}`
          : null;
        all.push({
          id: `sla-violation-${v.id}`,
          icon: v.status === 'escalated' ? '🚨' : '⏱',
          title: ruleNames[v.sla_rule] || `SLA Rule #${v.sla_rule}`,
          subtitle: `${entityLabel} — triggered ${daysSince(v.triggered_at)}d ago`,
          severity: sev,
          to,
          age: daysSince(v.triggered_at),
        });
      });

      const order = { critical: 0, high: 1, medium: 2, low: 3 };
      all.sort((a, b) => order[a.severity] - order[b.severity]);
      const sum = { critical: 0, high: 0, medium: 0, low: 0 };
      all.forEach(a => { if (sum[a.severity] !== undefined) sum[a.severity]++; });
      setAlerts(all);
      setSummary(sum);
      setLoading(false);
    };

    // Step 1: render cached data immediately
    const cachedMap = {};
    let hasCached = false;
    for (const [key, url] of ENTRIES) {
      const cached = listCache.get(url);
      if (cached) { cachedMap[key] = cached.data; hasCached = true; }
    }
    if (hasCached) buildAndSet(cachedMap);

    // Step 2: background-revalidate stale or missing entries
    const toFetch = ENTRIES.filter(([, url]) => { const e = listCache.get(url); return !e || e.isStale; });
    if (toFetch.length === 0) return;

    Promise.allSettled(
      toFetch.map(([, url]) =>
        listCache.fetchDeduped(url, {}).then(data => { listCache.set(url, data); return data; })
      )
    ).then(() => {
      // Rebuild from the now-fresh cache
      const freshMap = {};
      for (const [key, url] of ENTRIES) {
        const cached = listCache.get(url);
        if (cached) freshMap[key] = cached.data;
      }
      buildAndSet(freshMap);
    });
  }, []);

  const total = Object.values(summary).reduce((s, n) => s + n, 0);
  const totalPages = Math.ceil(alerts.length / pageSize);
  const pageAlerts = alerts.slice((page - 1) * pageSize, page * pageSize);
  const rangeStart = alerts.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd   = Math.min(page * pageSize, alerts.length);

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">SLA &amp; Escalation Alerts</h1>
          <p className="page-subtitle">Items requiring immediate attention</p>
        </div>
      </div>

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {Object.entries(SEVERITY).map(([key, s]) => (
          <div key={key} style={{
            background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8,
            padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 100,
          }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: s.fg, fontVariantNumeric: 'tabular-nums' }}>
              {loading ? '…' : summary[key]}
            </span>
            <span style={{ fontSize: 11, color: s.fg, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {s.label}
            </span>
          </div>
        ))}
        <div style={{
          background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 8,
          padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 100,
        }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-main)', fontVariantNumeric: 'tabular-nums' }}>
            {loading ? '…' : total}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-sub)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Total
          </span>
        </div>
      </div>

      {/* Alert list */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
        <div style={{
          padding: '13px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-main)' }}>
            {loading ? 'Scanning…' : `${total} alert${total !== 1 ? 's' : ''} found`}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {!loading && total > 0 && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Showing {rangeStart}–{rangeEnd} of {total}
              </span>
            )}
            {!loading && total > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Per page:</label>
                <select
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  style={{
                    fontSize: 12, padding: '3px 8px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--surface)',
                    color: 'var(--text-main)', cursor: 'pointer',
                  }}
                >
                  {[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, padding: '14px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
              <div className="skeleton" style={{ width: 36, height: 36, borderRadius: 8 }} />
              <div style={{ flex: 1 }}>
                <div className="skeleton" style={{ height: 13, width: '40%', borderRadius: 4, marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 11, width: '60%', borderRadius: 4 }} />
              </div>
              <div className="skeleton" style={{ height: 22, width: 60, borderRadius: 99 }} />
            </div>
          ))
        ) : alerts.length === 0 ? (
          <div className="empty-state" style={{ padding: '48px 24px' }}>
            <div className="empty-state-icon">✅</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-main)', marginBottom: 6 }}>All clear</div>
            <div className="empty-state-text">No escalation alerts at this time.</div>
          </div>
        ) : (
          pageAlerts.map(alert => (
            <AlertRow key={alert.id} {...alert} />
          ))
        )}

        {/* Pagination footer */}
        {!loading && totalPages > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 20px', borderTop: '1px solid var(--border)',
            background: 'var(--surface)',
          }}>
            <button
              className="btn btn-ghost btn-sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              ← Previous
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Page {page} of {totalPages}
            </span>
            <button
              className="btn btn-ghost btn-sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Info note */}
      <div className="alert alert-info" style={{ marginTop: 16 }}>
        Alerts are generated from overdue tasks, pending follow-ups, and leads inactive for 14+ days.
        Connect the backend analytics endpoint to enable real-time SLA tracking.
      </div>
    </div>
  );
};

export default SLAAlerts;
